import React, { useState, useEffect, useCallback, useRef } from 'react';
import { TEMPLATES, generateRandomGrid } from '../game/mapGenerator';
import { GRID_COLS, GRID_ROWS, CELL, HUB, TILE_SIZE } from '../game/config';
import { getLayout, saveLayout, deleteLayout, getEditorAssets, uploadEditorAsset, deleteEditorAsset } from '../services/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Save, Trash2, Plus, Upload, X, RefreshCw, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';

const CELL_SIZE = 30;

const BUILT_IN_DECORATIONS = [
  { key: 'decor_1x1', tw: 1, th: 1, src: '/assets/tiles/decor_warehouse_1x1_01.png' },
  { key: 'decor_2x1', tw: 2, th: 1, src: '/assets/tiles/decor_warehouse_2x1_01.png' },
  { key: 'decor_2x2', tw: 2, th: 2, src: '/assets/tiles/decor_warehouse_2x2_01.png' },
  { key: 'decor_3x1', tw: 3, th: 1, src: '/assets/tiles/decor_warehouse_3x1_01.png' },
  { key: 'decor_3x2', tw: 3, th: 2, src: '/assets/tiles/decor_warehouse_3x2_01.png' },
  { key: 'decor_4x1', tw: 4, th: 1, src: '/assets/tiles/decor_warehouse_4x1_01.png' },
  { key: 'decor_platform', tw: 4, th: 1, src: '/assets/tiles/decor_platform_4x1_01.png' },
  { key: 'decor_hub_lg', tw: 5, th: 4, src: '/assets/tiles/decor_hub_center.png' },
];

const BUILT_IN_PROPS = [
  { key: 'prop_01', tw: 1, th: 1, src: '/assets/tiles/prop_icon_01.png' },
  { key: 'prop_02', tw: 1, th: 1, src: '/assets/tiles/prop_icon_02.png' },
  { key: 'prop_03', tw: 1, th: 1, src: '/assets/tiles/prop_icon_03.png' },
  { key: 'prop_04', tw: 1, th: 1, src: '/assets/tiles/prop_icon_04.png' },
  { key: 'prop_2x1', tw: 2, th: 1, src: '/assets/tiles/prop_icon_2x1_1.png' },
];

const LevelEditor = () => {
  const [templateId, setTemplateId] = useState(0);
  const [placements, setPlacements] = useState([]);
  const [customAssets, setCustomAssets] = useState([]);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadForm, setUploadForm] = useState({ name: '', tw: 1, th: 1, type: 'decoration', file: null });
  const [uploadPreview, setUploadPreview] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const fileInputRef = useRef(null);
  const [customGrid, setCustomGrid] = useState(null);
  const [hoveredCell, setHoveredCell] = useState(null);
  const [dragging, setDragging] = useState(null); // { asset, startX, startY }
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const gridRef = useRef(null);

  const grid = customGrid || TEMPLATES[templateId].map(r => [...r]);

  // Load layout + custom assets
  const loadData = useCallback(async () => {
    try {
      const [layout, assets] = await Promise.all([getLayout(templateId), getEditorAssets()]);
      setPlacements(layout.placements || []);
      setCustomGrid(layout.grid?.length === GRID_ROWS ? layout.grid : null);
      setCustomAssets(assets || []);
      setIsDirty(false);
    } catch (e) { console.error('Error loading layout:', e); }
  }, [templateId]);

  useEffect(() => { loadData(); }, [loadData]);

  const allDecorations = [
    ...BUILT_IN_DECORATIONS,
    ...customAssets.filter(a => a.asset_type === 'decoration').map(a => ({ key: a.id, tw: a.tw, th: a.th, src: a.src, custom: true }))
  ];
  const allProps = [
    ...BUILT_IN_PROPS,
    ...customAssets.filter(a => a.asset_type === 'prop').map(a => ({ key: a.id, tw: a.tw, th: a.th, src: a.src, custom: true }))
  ];

  const canPlace = (asset, col, row, excludeIdx = -1) => {
    for (let dr = 0; dr < asset.th; dr++) {
      for (let dc = 0; dc < asset.tw; dc++) {
        const c = col + dc, r = row + dr;
        if (c >= GRID_COLS || r >= GRID_ROWS) return false;
        const cell = grid[r][c];
        // Allow placement on WALL cells and HUB cells
        if (cell !== CELL.WALL && cell !== CELL.HUB) return false;
        if (r === 0 || r === GRID_ROWS - 1) return false;
      }
    }
    for (let i = 0; i < placements.length; i++) {
      if (i === excludeIdx) continue;
      const p = placements[i];
      if (col < p.col + p.tw && col + asset.tw > p.col && row < p.row + p.th && row + asset.th > p.row) return false;
    }
    return true;
  };

  // ── Custom drag system (no HTML5 drag API) ──
  
  // Start drag from palette
  const handleDragStartCustom = (e, asset) => {
    e.preventDefault();
    setDragging({ asset });
    setMousePos({ x: e.clientX, y: e.clientY });
  };

  // Global mouse move + mouse up (attached when dragging)
  useEffect(() => {
    if (!dragging) return;
    const onMove = (e) => {
      setMousePos({ x: e.clientX, y: e.clientY });
      if (gridRef.current) {
        const rect = gridRef.current.getBoundingClientRect();
        const c = Math.floor((e.clientX - rect.left) / CELL_SIZE);
        const r = Math.floor((e.clientY - rect.top) / CELL_SIZE);
        if (c >= 0 && c < GRID_COLS && r >= 0 && r < GRID_ROWS) {
          setHoveredCell({ c, r });
        } else {
          setHoveredCell(null);
        }
      }
    };
    const onUp = (e) => {
      if (gridRef.current && dragging) {
        const rect = gridRef.current.getBoundingClientRect();
        const col = Math.floor((e.clientX - rect.left) / CELL_SIZE);
        const row = Math.floor((e.clientY - rect.top) / CELL_SIZE);
        if (col >= 0 && col < GRID_COLS && row >= 0 && row < GRID_ROWS) {
          const cell = grid[row]?.[col];
          if ((cell === CELL.WALL || cell === CELL.HUB) && canPlace(dragging.asset, col, row)) {
            setPlacements(prev => [...prev, {
              asset_key: dragging.asset.key, col, row, tw: dragging.asset.tw, th: dragging.asset.th,
              asset_type: dragging.asset.key.startsWith('prop') ? 'prop' : 'decoration'
            }]);
            setIsDirty(true);
          }
        }
      }
      setDragging(null);
      setHoveredCell(null);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, [dragging, grid]); // eslint-disable-line react-hooks/exhaustive-deps

  // Click on grid to remove placed asset
  const handleGridClick = (e) => {
    if (dragging) return; // ignore during drag
    if (!gridRef.current) return;
    const rect = gridRef.current.getBoundingClientRect();
    const col = Math.floor((e.clientX - rect.left) / CELL_SIZE);
    const row = Math.floor((e.clientY - rect.top) / CELL_SIZE);
    if (col < 0 || col >= GRID_COLS || row < 0 || row >= GRID_ROWS) return;
    const existingIdx = placements.findIndex(p =>
      col >= p.col && col < p.col + p.tw && row >= p.row && row < p.row + p.th
    );
    if (existingIdx !== -1) {
      setPlacements(prev => prev.filter((_, i) => i !== existingIdx));
      setIsDirty(true);
    }
  };

  const handleSave = async () => {
    try { await saveLayout(templateId, placements, customGrid); setIsDirty(false); toast.success(`Layout Template ${templateId + 1} salvato!`); }
    catch { toast.error('Errore salvataggio'); }
  };

  const handleClear = () => { setPlacements([]); setIsDirty(true); };

  const handleRandomize = () => {
    const newGrid = generateRandomGrid();
    setCustomGrid(newGrid);
    setPlacements([]); // clear decorations — new grid, new layout
    setIsDirty(true);
    toast.success('Mappa generata! Salva per utilizzarla nel gioco.');
  };

  const handleDelete = async () => {
    try { await deleteLayout(templateId); setPlacements([]); setCustomGrid(null); setIsDirty(false); toast.success('Layout eliminato'); }
    catch { toast.error('Errore eliminazione'); }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Create preview and auto-detect dimensions
    const url = URL.createObjectURL(file);
    const img = new window.Image();
    img.onload = () => {
      const tw = Math.max(1, Math.round(img.width / TILE_SIZE));
      const th = Math.max(1, Math.round(img.height / TILE_SIZE));
      setUploadForm(p => ({ ...p, file, tw, th, name: p.name || file.name.replace(/\.png$/i, '') }));
      setUploadPreview({ url, width: img.width, height: img.height, tw, th });
    };
    img.onerror = () => {
      setUploadForm(p => ({ ...p, file }));
      setUploadPreview(null);
      toast.error('Impossibile leggere le dimensioni dell\'immagine');
    };
    img.src = url;
  };

  const handleUploadAsset = async () => {
    if (!uploadForm.file || !uploadForm.name) { toast.error('Seleziona un file e inserisci un nome'); return; }
    setUploadLoading(true);
    try {
      await uploadEditorAsset(uploadForm.file, uploadForm.name, uploadForm.tw, uploadForm.th, uploadForm.type);
      setShowUploadDialog(false);
      setUploadForm({ name: '', tw: 1, th: 1, type: 'decoration', file: null });
      if (uploadPreview?.url) URL.revokeObjectURL(uploadPreview.url);
      setUploadPreview(null);
      const assets = await getEditorAssets();
      setCustomAssets(assets || []);
      toast.success('Asset caricato!');
    } catch (err) {
      console.error('Upload error:', err);
      toast.error('Errore durante il caricamento: ' + (err.response?.data?.detail || err.message));
    } finally {
      setUploadLoading(false);
    }
  };

  const handleCloseUploadDialog = () => {
    setShowUploadDialog(false);
    setUploadForm({ name: '', tw: 1, th: 1, type: 'decoration', file: null });
    if (uploadPreview?.url) URL.revokeObjectURL(uploadPreview.url);
    setUploadPreview(null);
  };

  const handleDeleteCustomAsset = async (assetId) => {
    try {
      await deleteEditorAsset(assetId);
      setPlacements(prev => prev.filter(p => p.asset_key !== assetId));
      const assets = await getEditorAssets();
      setCustomAssets(assets || []);
      toast.success('Asset eliminato');
    } catch { toast.error('Errore'); }
  };

  const getAssetSrc = (key) => {
    const all = [...BUILT_IN_DECORATIONS, ...BUILT_IN_PROPS, ...customAssets.map(a => ({ key: a.id, src: a.src }))];
    return all.find(a => a.key === key)?.src || '';
  };

  const cellBg = (cell) => {
    if (cell === CELL.WALL) return '#6B7280';
    if (cell === CELL.HUB) return '#1E3A5F';
    return '#1F2937';
  };

  // Ghost highlight: show where the asset would land
  const isInGhostArea = (col, row) => {
    if (!hoveredCell || !dragging) return false;
    return col >= hoveredCell.c && col < hoveredCell.c + dragging.asset.tw &&
           row >= hoveredCell.r && row < hoveredCell.r + dragging.asset.th;
  };

  // Palette item — mousedown starts custom drag
  const PaletteItem = ({ asset, borderColor }) => (
    <div className="relative group">
      <div
        onMouseDown={(e) => handleDragStartCustom(e, asset)}
        className={`p-1 bg-white border-2 ${borderColor} rounded-lg cursor-grab active:cursor-grabbing hover:shadow-md hover:scale-105 transition-all select-none`}
        style={{ width: asset.tw * 40 + 8, height: asset.th * 40 + 8 }}
        data-testid={`palette-${asset.key}`}
      >
        <img src={asset.src} alt={asset.key} className="w-full h-full object-contain pointer-events-none select-none" style={{ imageRendering: 'pixelated' }} draggable={false} />
      </div>
      <div className="text-[9px] text-gray-400 text-center mt-0.5">{asset.tw}x{asset.th}</div>
      {asset.custom && (
        <button onClick={(e) => { e.stopPropagation(); handleDeleteCustomAsset(asset.key); }} className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-[8px] opacity-0 group-hover:opacity-100 transition">
          <X className="w-2.5 h-2.5" />
        </button>
      )}
    </div>
  );

  return (
    <div className="space-y-3" data-testid="level-editor">

      {/* Floating drag preview */}
      {dragging && (
        <div
          className="fixed pointer-events-none z-[9999]"
          style={{
            left: mousePos.x - (dragging.asset.tw * CELL_SIZE) / 2,
            top: mousePos.y - (dragging.asset.th * CELL_SIZE) / 2,
            width: dragging.asset.tw * CELL_SIZE,
            height: dragging.asset.th * CELL_SIZE,
            opacity: 0.75,
          }}
        >
          <img src={dragging.asset.src} alt="" className="w-full h-full object-contain" style={{ imageRendering: 'pixelated' }} draggable={false} />
        </div>
      )}

      {/* Header */}
      <div className="flex items-center gap-3 flex-wrap">
        <span className="font-semibold text-sm">Template:</span>
        {TEMPLATES.map((_, i) => (
          <Button key={i} variant={templateId === i ? 'default' : 'outline'} size="sm" onClick={() => setTemplateId(i)} data-testid={`template-btn-${i}`}>
            Livello {i + 1}
          </Button>
        ))}
        <div className="ml-auto flex gap-2">
          <Button size="sm" variant="outline" onClick={handleRandomize} data-testid="randomize-btn" title="Genera mappa random">
            <RefreshCw className="w-4 h-4 mr-1" /> Random
          </Button>
          <Button size="sm" onClick={handleSave} disabled={!isDirty} className="bg-[#005EB8]" data-testid="save-layout-btn">
            <Save className="w-4 h-4 mr-1" /> Salva
          </Button>
          <Button size="sm" variant="outline" onClick={handleClear} data-testid="clear-layout-btn">Pulisci</Button>
          <Button size="sm" variant="destructive" onClick={handleDelete} data-testid="delete-layout-btn">
            <Trash2 className="w-4 h-4 mr-1" /> Elimina
          </Button>
        </div>
      </div>

      {isDirty && <div className="text-xs text-amber-600 bg-amber-50 px-3 py-1 rounded">Modifiche non salvate</div>}

      <div className="flex gap-4">
        {/* Palette */}
        <div className="w-52 flex-shrink-0 space-y-3 max-h-[780px] overflow-y-auto overflow-x-hidden pr-1">
          <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Decorazioni</div>
          <div className="flex flex-wrap gap-2">
            {allDecorations.map(a => <PaletteItem key={a.key} asset={a} borderColor="border-blue-200 hover:border-blue-400" />)}
          </div>

          <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mt-3">Props</div>
          <div className="flex flex-wrap gap-2">
            {allProps.map(a => <PaletteItem key={a.key} asset={a} borderColor="border-orange-200 hover:border-orange-400" />)}
          </div>

          <Button size="sm" variant="outline" className="w-full mt-2" onClick={() => setShowUploadDialog(true)} data-testid="add-asset-btn">
            <Upload className="w-4 h-4 mr-1" /> Carica Asset
          </Button>

          <div className="text-[10px] text-gray-400 mt-2 space-y-0.5">
            <p><strong>Trascina</strong> un asset sulla griglia</p>
            <p><strong>Click</strong> su un asset posizionato per rimuoverlo</p>
          </div>
        </div>

        {/* Grid */}
        <div className="flex-1 overflow-auto">
          <div
            ref={gridRef}
            className="relative border border-gray-300 bg-black"
            style={{ width: GRID_COLS * CELL_SIZE, height: GRID_ROWS * CELL_SIZE }}
            data-testid="editor-grid"
            onClick={handleGridClick}
          >
            {grid.map((row, r) =>
              row.map((cell, c) => {
                const inGhost = isInGhostArea(c, r);
                const isDroppable = cell === CELL.WALL || cell === CELL.HUB;
                return (
                  <div
                    key={`${c}-${r}`}
                    className="absolute pointer-events-none"
                    style={{
                      left: c * CELL_SIZE, top: r * CELL_SIZE,
                      width: CELL_SIZE, height: CELL_SIZE,
                      backgroundColor: inGhost ? (isDroppable ? 'rgba(59,130,246,0.45)' : 'rgba(239,68,68,0.3)') : cellBg(cell),
                      border: '1px solid rgba(0,0,0,0.08)',
                    }}
                  />
                );
              })
            )}

            {/* Placed assets */}
            {placements.map((p, i) => (
              <div
                key={`p-${i}`}
                className="absolute pointer-events-none"
                style={{
                  left: p.col * CELL_SIZE, top: p.row * CELL_SIZE,
                  width: p.tw * CELL_SIZE, height: p.th * CELL_SIZE,
                  zIndex: 10,
                }}
              >
                <img src={getAssetSrc(p.asset_key)} alt={p.asset_key} className="w-full h-full object-contain" style={{ imageRendering: 'pixelated' }} draggable={false} />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Upload Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={(open) => { if (!open) handleCloseUploadDialog(); else setShowUploadDialog(true); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Aggiungi Asset Personalizzato</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {/* File selector - prominent */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">File PNG</label>
              <div 
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50/50 transition-colors"
                data-testid="upload-dropzone"
              >
                {uploadPreview ? (
                  <div className="flex flex-col items-center gap-2">
                    <img src={uploadPreview.url} alt="Anteprima" className="max-h-24 object-contain" style={{ imageRendering: 'pixelated' }} />
                    <div className="text-xs text-gray-500">
                      {uploadPreview.width}x{uploadPreview.height}px → Griglia rilevata: <strong className="text-blue-600">{uploadPreview.tw}x{uploadPreview.th}</strong>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-1 text-gray-400">
                    <ImageIcon className="w-8 h-8" />
                    <span className="text-sm">Clicca per selezionare un file PNG</span>
                    <span className="text-xs">La dimensione della griglia verrà rilevata automaticamente</span>
                  </div>
                )}
              </div>
              <input 
                ref={fileInputRef}
                type="file" 
                accept="image/png,image/jpeg,image/webp" 
                onChange={handleFileSelect}
                className="hidden"
                data-testid="upload-file-input"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">Nome</label>
              <Input value={uploadForm.name} onChange={e => setUploadForm(p => ({ ...p, name: e.target.value }))} placeholder="Es: Warehouse Grande" data-testid="upload-name-input" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm text-gray-600">Larghezza (tiles)</label>
                <Input type="number" min={1} max={10} value={uploadForm.tw} onChange={e => setUploadForm(p => ({ ...p, tw: parseInt(e.target.value) || 1 }))} data-testid="upload-tw-input" />
              </div>
              <div>
                <label className="text-sm text-gray-600">Altezza (tiles)</label>
                <Input type="number" min={1} max={10} value={uploadForm.th} onChange={e => setUploadForm(p => ({ ...p, th: parseInt(e.target.value) || 1 }))} data-testid="upload-th-input" />
              </div>
            </div>

            <div>
              <label className="text-sm text-gray-600">Tipo</label>
              <select className="w-full border rounded px-3 py-2 text-sm" value={uploadForm.type} onChange={e => setUploadForm(p => ({ ...p, type: e.target.value }))} data-testid="upload-type-select">
                <option value="decoration">Decorazione</option>
                <option value="prop">Prop</option>
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCloseUploadDialog} data-testid="upload-cancel-btn">Annulla</Button>
            <Button onClick={handleUploadAsset} className="bg-[#005EB8]" disabled={!uploadForm.file || !uploadForm.name || uploadLoading} data-testid="upload-submit-btn">
              {uploadLoading ? (
                <span className="flex items-center gap-1"><RefreshCw className="w-4 h-4 animate-spin" /> Caricamento...</span>
              ) : (
                <span className="flex items-center gap-1"><Upload className="w-4 h-4" /> Carica</span>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LevelEditor;
